import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAllowedRoutes } from '@/hooks/useAllowedRoutes';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface RouteGuardProps {
  children: ReactNode;
}

function AccessDenied() {
  const { user } = useAuth();
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4 text-center px-4">
      <div className="rounded-full bg-destructive/10 p-4">
        <ShieldAlert className="h-10 w-10 text-destructive" />
      </div>
      <h2 className="text-xl font-semibold text-foreground">Pristup odbijen</h2>
      <p className="text-sm text-muted-foreground max-w-md">
        Nemate dozvolu za pristup ovoj stranici. Kontaktirajte administratora ako smatrate da bi trebali imati pristup.
      </p>
      <p className="text-xs text-muted-foreground">
        Uloga: <span className="font-medium capitalize">{user?.role || 'N/A'}</span>
      </p>
      <Button variant="outline" onClick={() => window.history.back()} className="mt-2">
        Nazad
      </Button>
    </div>
  );
}

/**
 * Wraps route content and checks if the current path is allowed.
 * - Admin bypasses all checks.
 * - While loading, shows a spinner (no content leak).
 * - If resolved and denied, shows Access Denied screen.
 */
export function RouteGuard({ children }: RouteGuardProps) {
  const { user } = useAuth();
  const location = useLocation();
  const { routes: allowedRoutes, isLoading } = useAllowedRoutes();

  const hasAllowedRoute = (route: string) => allowedRoutes?.has(route) ?? false;
  const hasAnyAllowedRoute = (routes: string[]) => routes.some(hasAllowedRoute);

  // Admin always passes
  if (user?.role === 'admin') return <>{children}</>;

  // While loading permissions, show spinner (no content leak)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Admin null case (shouldn't reach here but safety)
  if (allowedRoutes === null) return <>{children}</>;

  const path = location.pathname;

  // Exact match
  if (hasAllowedRoute(path)) return <>{children}</>;

  // Check parent paths (e.g. /lists/abc123 allowed if /lists is allowed)
  for (const allowed of allowedRoutes) {
    if (path.startsWith(allowed + '/')) return <>{children}</>;
  }

  // Detail pages that inherit parent permission.
  // Client profiles stay reachable from Work Screen even when My Clients nav is hidden.
  const detailPatterns = [
    { pattern: /^\/client\//, requiresAny: ['/deals', '/work'] },
    { pattern: /^\/clients\//, requiresAny: ['/deals', '/work'] },
    { pattern: /^\/deals\//, requiresAny: ['/deals'] },
    { pattern: /^\/agent\//, requiresAny: ['/team-agents/agents'] },
    { pattern: /^\/lists\/[^/]+$/, requiresAny: ['/lists'] },
  ];

  for (const { pattern, requiresAny } of detailPatterns) {
    if (pattern.test(path) && hasAnyAllowedRoute(requiresAny)) {
      return <>{children}</>;
    }
  }

  // Access denied
  return <AccessDenied />;
}
